import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { FiChevronDown, FiMessageSquare, FiUsers } from 'react-icons/fi';
import { t } from 'i18next';
import { useCheckIncludeProfiles } from '@/app/hooks/useAuthorityLevel';
import Dropdown from 'src/app/components/Dropdown';
import moment from 'moment';
import { useAuditorium } from 'src/app/hooks/useAuditorium';
import { useFormik } from 'formik';
import { useSession } from 'src/app/hooks/useSession';
import usePermission from '@/app/hooks/usePermission';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardBody,
  CardBodyContent,
  CardBodyHeader,
  CardBodyHeaderContent,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardIcon,
  CardIconImg,
  CardIconVerified,
  CardKpi,
  CardKpiGrid,
  CardKpiTitle,
  CardTitle
} from '@/components/ui/card';

export function AuditoriumCard({ auditorium, setActiveAuditorium }) {
  const { deleteAuditorium, updateAuditorium } = useAuditorium();
  const isAdmin = !useCheckIncludeProfiles({ only: ['student'] });

  const currentUser = useSession().session.user;

  const auditoriumForm = useFormik({
    initialValues: {
      published: auditorium?.published
    }
  });

  const badgeColor = auditorium.status === 'Online' ? 'success' : 'warning';

  const handleChangePublishedAuditorium = async () => {
    auditoriumForm.setFieldValue('published', !auditoriumForm.values.published);
    await updateAuditorium({ published: !auditoriumForm.values.published }, auditorium.id);
  };

  const shouldRenderOptions = useMemo(() => {
    if (auditorium?.['authored-by-user'] === true) {
      if (auditorium?.creator?.id !== currentUser.id) {
        return false;
      } else return true;
    }

    if (auditorium?.['authored-by-user'] === false) {
      if (usePermission('Editar monitorias', 'Monitorias') || usePermission('Excluir monitorias', 'Monitorias')) {
        return true;
      } else return false;
    }
  }, [auditorium]);

  return (
    <Card hover>
      {isAdmin && (
        <CardHeader className="flex-row">
          <div className="form__check form__switch !mb-0 grow">
            <input
              className="form__check-input"
              id={`published-${auditorium.id}`}
              name={`published-${auditorium.id}`}
              type="checkbox"
              defaultChecked={auditoriumForm.values.published}
              onClick={handleChangePublishedAuditorium}
            />
            <label htmlFor={`published-${auditorium.id}`}>Publicar</label>
          </div>

          <span className="card__text-schedule">{auditorium['published-at'] && `Publicação agendada para ${moment(auditorium['published-at']).format('DD/MM/YYYY')}`}</span>
        </CardHeader>
      )}

      <CardContent>
        <CardIcon>
          {auditorium?.['official-content'] && <CardIconVerified />}
          <CardIconImg>
            <FiMessageSquare />
          </CardIconImg>
        </CardIcon>

        <CardBody>
          <CardBodyHeader>
            <CardBodyHeaderContent>
              <CardTitle title={auditorium.name}>{auditorium.name}</CardTitle>
            </CardBodyHeaderContent>

            <CardDescription title={auditorium?.['auditorium-lesson']?.name}>{auditorium?.['auditorium-lesson']?.name}</CardDescription>

            <Badge
              variant={badgeColor}
              size="xs"
            >
              {auditorium.status}
            </Badge>
          </CardBodyHeader>

          <CardBodyContent>
            <CardKpiGrid>
              <CardKpi
                clickable
                onClick={() => setActiveAuditorium(auditorium)}
              >
                <CardKpiTitle>{t('textsCommon.participants')}</CardKpiTitle>
                <FiUsers /> {auditorium?.['student-count']}
              </CardKpi>
              <CardKpi>
                <CardKpiTitle>{t('projects.updatedIn')}</CardKpiTitle>
                {moment(auditorium?.['auditorium-lesson'] ? auditorium?.['auditorium-lesson']?.['updated-at'] : auditorium['updated-at']).format('DD/MM/YYYY')}
              </CardKpi>
            </CardKpiGrid>
          </CardBodyContent>
        </CardBody>
      </CardContent>

      <CardFooter>
        {!useCheckIncludeProfiles({ only: ['student'] }) ? (
          <div
            className="btn-group btn-group--with-dropdown"
            role="group"
            aria-label="Ações"
          >
            <Link
              to={`/auditorio/${auditorium.id}`}
              className="btn btn--outline btn--small"
            >
              {t('button.access')}
            </Link>
            {shouldRenderOptions && (
              <Dropdown className="btn--small btn btn--outline">
                <Dropdown.Toggle>
                  <FiChevronDown />
                </Dropdown.Toggle>

                <Dropdown.Content>
                  {usePermission('Editar monitorias', 'Monitorias') && <Dropdown.Item to={`/auditorio/editar/nome/${auditorium.id}`}>{t('button.edit')}</Dropdown.Item>}
                  {usePermission('Excluir monitorias', 'Monitorias') && <Dropdown.Item onClick={() => deleteAuditorium(auditorium.id)}>{t('button.delete')}</Dropdown.Item>}
                </Dropdown.Content>
              </Dropdown>
            )}
          </div>
        ) : (
          <Button
            variant="outline"
            size="sm"
            wide
            fullWidth
            asChild
          >
            <Link to={`/auditorio/${auditorium.id}`}>{t('button.access')}</Link>
          </Button>
        )}
      </CardFooter>
    </Card>
  );
}
